import * as monaco from 'monaco-editor'
import { debounce } from 'lodash'

var editor: monaco.editor.IStandaloneCodeEditor

const ENABLE_WEBDAV = process.env.ENABLE_WEBDAV

declare global {
  interface Window {
    MonacoEnvironment?: monaco.Environment
    useWebDav: boolean
    path: string
  }
}

window.MonacoEnvironment = {
  getWorkerUrl: function () {
    return './editor.worker.bundle.js'
  },
}

const setLastCode = async (code: string): Promise<boolean> => {
  if (ENABLE_WEBDAV) {
    try {
      const response = await fetch(window.path, { method: 'PUT', body: code })
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`)
      }
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  if (!window.localStorage) {
    return false
  }

  try {
    window.localStorage.setItem('monaco-editor-lastcode', code)
    return true
  } catch (error) {
    console.error(error)
    return false
  }
}

const getLastCode = async () => {
  if (ENABLE_WEBDAV) {
    const propfindResponse = await fetch(window.path, { method: 'PROPFIND' })
    if (propfindResponse.status !== 207) {
      return undefined
    }
    try {
      const response = await fetch(window.path)
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`)
      }
      return await response.text()
    } catch (error) {
      console.error(error)
    }
  } else if (window.localStorage && window.localStorage.getItem('monaco-editor-lastcode')) {
    return window.localStorage.getItem('monaco-editor-lastcode')
  }

  return ''
}

export const getCode = () => {
  return editor.getValue()
}

export const setFocusToMonacoEditor = () => {
  editor.focus()
}

export async function initializeMonacoEditor(codeEditorId: string) {
  const initialCode = await getLastCode()
  const editorContainer = document.getElementById(codeEditorId)
  if (!editorContainer) {
    return
  }

  // Localized save status elements (text lives in app.html, toggled by language)
  const saveStatusElements = {
    saving: document.getElementById('save-indicator-saving'),
    saved: document.getElementById('save-indicator-saved'),
    error: document.getElementById('save-indicator-error'),
  }
  const showSaveStatus = (status: keyof typeof saveStatusElements) => {
    for (const [key, element] of Object.entries(saveStatusElements)) {
      element?.classList.toggle('hidden', key !== status)
    }
  }

  editor = monaco.editor.create(document.getElementById(codeEditorId), {
    automaticLayout: true,
    language: 'python',
    lineNumbersMinChars: 2,
    minimap: {
      enabled: false,
    },
    scrollBeyondLastLine: false,
    tabSize: 4,
    renderWhitespace: 'all',
    value: initialCode,
    wordWrap: 'on',
  })

  // overwrite some keybindings
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyR, () => {})
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyC, () => {})
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyW, () => {})

  if (ENABLE_WEBDAV) {
    // Auto save
    editor.onDidChangeModelContent(() => {
      showSaveStatus('saving')
      debouncedSave(editor.getValue())
    })
  } else {
    document.getElementById(codeEditorId)?.addEventListener('keyup', () => {
      void setLastCode(editor.getValue()).catch((error) => {
        console.error(error)
      })
    })
  }

  // Auto save with 1 second debounce
  const debouncedSave = debounce(async (value: string) => {
    const saved = await setLastCode(value)
    showSaveStatus(saved ? 'saved' : 'error')
  }, 1000)

  document
    .getElementById('tab-programming-ide-container')
    .addEventListener('replaceEditorTextForTestingPurposes', (event: CustomEvent) => {
      editor.setValue(event.detail.text)
    })
}
